import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import React from 'react';
import { Link } from 'react-router-dom';

import { useGetBeneficiaryVirtualAccountsQuery, useGetOwnIntegratorFeaturesQuery } from '@/api/v1';
import DepositIcon from '@/assets/icons/deposit-icon.svg?react';
import InfoIcon from '@/assets/icons/info-icon.svg?react';
import TransferIcon from '@/assets/icons/send-icon.svg?react';
import { EmptyState } from '@/components/atoms/EmptyState';
import { BalanceWidget } from '@/components/molecules/BalanceWidget';
import { FF_DASHBOARD_ALLOW_VA_CREATION_FOR_INDIVIDUALS } from '@/data/featureFlags';
import { useBalances } from '@/hooks/useBalances';
import { useIntegratorFeatureFlag } from '@/hooks/useFeatureFlags';
import { usePermissionsManager } from '@/hooks/usePermissionsManager';
import { useTranslator } from '@/hooks/useTranslator';
import { CreateUsdAccountModal } from '@/Screens/Dashboard/Customers/CreateUsdAccountModal';
import { DetailedBalance } from '@/types/bankInfo';
import { CurrencyCodeType } from '@/types/currency';
import { BeneficiaryType } from '@/types/customer';
import { CustomerKYXVerification } from '@/types/customerKYXVerification';
import { Permission } from '@/types/permissions';

interface Props {
  readonly beneficiaryId: string;
  readonly beneficiaryType: CustomerKYXVerification['type'];
  readonly depositLink?: string;
  readonly transferLink?: string;
}

const BALANCE_CURRENCIES = [
  CurrencyCodeType.USD,
  CurrencyCodeType.USDC,
  CurrencyCodeType.USDT,
] as const;

export const CustomerBalanceSection: React.FC<Props> = (props: Props): React.ReactElement => {
  const { beneficiaryId, beneficiaryType, depositLink, transferLink } = props;
  const tr = useTranslator();
  const { userIsAuthorizedTo } = usePermissionsManager();
  const [isCreateUSDVirtualAccountModalOpen, setIsCreateUSDVirtualAccountModalOpen] =
    React.useState(false);
  const { data: virtualAccounts = [], refetch: refetchBeneficiaryVirtualAccounts } =
    useGetBeneficiaryVirtualAccountsQuery(beneficiaryId);
  const hasUsdVirtualAccount = virtualAccounts.length > 0;
  const { data: integratorFeatures, isLoading: isLoadingIntegratorFeatures } =
    useGetOwnIntegratorFeaturesQuery();
  const isVaEnabled = integratorFeatures?.vaEnabled ?? false;
  const allowVaCreationForIndividuals = useIntegratorFeatureFlag(
    FF_DASHBOARD_ALLOW_VA_CREATION_FOR_INDIVIDUALS,
  );
  const canCreateUsdVirtualAccount =
    beneficiaryType === BeneficiaryType.business || allowVaCreationForIndividuals;
  const balances = useBalances({
    currencies: BALANCE_CURRENCIES,
    beneficiaryId,
  });
  const canViewUsdAccount = userIsAuthorizedTo(Permission.VIEW_USD_ACCOUNT);
  const balanceList = Object.values(balances).filter(
    (balance: DetailedBalance | undefined): balance is DetailedBalance =>
      balance !== undefined && (balance.currencyCode !== CurrencyCodeType.USD || canViewUsdAccount),
  );
  const hasBalances = balanceList.length > 0;

  const toggleCreateUSDVirtualAccountModalOpen = (): void =>
    setIsCreateUSDVirtualAccountModalOpen((prevState: boolean): boolean => !prevState);

  return (
    <>
      <Paper elevation={3} sx={styles.paper}>
        <Stack gap={3}>
          <Typography variant="h7">{tr('STABLECOIN_BALANCES')}</Typography>
          {hasBalances ? (
            <Stack gap={2}>
              {balanceList.map((balance: DetailedBalance): React.ReactElement => {
                const isUsdWithoutAccount =
                  balance.currencyCode === CurrencyCodeType.USD && !hasUsdVirtualAccount;

                return (
                  <Box key={balance.currencyCode} sx={styles.balanceRow}>
                    <Box sx={styles.balanceWidgetWrapper}>
                      <BalanceWidget
                        balance={balance}
                        showDetailsButton={!isUsdWithoutAccount}
                        beneficiaryId={isUsdWithoutAccount ? undefined : beneficiaryId}
                      />
                    </Box>
                    {isUsdWithoutAccount &&
                      canCreateUsdVirtualAccount &&
                      userIsAuthorizedTo(Permission.CREATE_USD_ACCOUNT) && (
                        <Box sx={styles.createButtonWrapper}>
                          <Tooltip
                            title={!isVaEnabled ? tr('CREATE_USD_ACCOUNT_DISABLED_TOOLTIP') : ''}
                            placement="top">
                            <Box component="span">
                              <Button
                                variant="contained"
                                size="compact"
                                disabled={!isVaEnabled}
                                endIcon={
                                  !isVaEnabled ? (
                                    <InfoIcon fill="currentColor" width={16} height={16} />
                                  ) : undefined
                                }
                                loading={isLoadingIntegratorFeatures}
                                loadingPosition="start"
                                onClick={toggleCreateUSDVirtualAccountModalOpen}>
                                {tr('CREATE_USD_ACCOUNT')}
                              </Button>
                            </Box>
                          </Tooltip>
                        </Box>
                      )}
                  </Box>
                );
              })}
            </Stack>
          ) : (
            <EmptyState.Container sx={styles.emptyStateContainer}>
              <EmptyState.Title variant="h7">{tr('NO_BALANCES_YET')}</EmptyState.Title>
            </EmptyState.Container>
          )}
        </Stack>
        {(transferLink || depositLink) && (
          <Box sx={styles.actionButtons}>
            {transferLink && (
              <ButtonBase component={Link} to={transferLink} sx={styles.actionButtonBase}>
                <Box sx={styles.actionButtonIcon}>
                  <TransferIcon width={12} height={12} stroke="currentColor" />
                </Box>
                <Typography variant="h8">{tr('TRANSFER')}</Typography>
              </ButtonBase>
            )}
            {depositLink && (
              <ButtonBase component={Link} to={depositLink} sx={styles.actionButtonBase}>
                <Box sx={styles.actionButtonIcon}>
                  <DepositIcon width={12} height={12} stroke="currentColor" />
                </Box>
                <Typography variant="h8">{tr('DEPOSIT')}</Typography>
              </ButtonBase>
            )}
          </Box>
        )}
      </Paper>
      <CreateUsdAccountModal
        open={isCreateUSDVirtualAccountModalOpen}
        beneficiaryId={beneficiaryId}
        onClose={toggleCreateUSDVirtualAccountModalOpen}
        onSuccess={refetchBeneficiaryVirtualAccounts}
      />
    </>
  );
};

const styles: Record<string, SystemStyleObject> = {
  paper: {
    p: 5,
    borderRadius: 2,
  },
  balanceRow: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },
  balanceWidgetWrapper: {
    flex: 1,
  },
  createButtonWrapper: {
    position: 'absolute',
    right: 24,
  },
  emptyStateContainer: {
    flex: 1,
    minHeight: 200,
    backgroundColor: 'custom.OffWhite',
    borderRadius: 2,
    px: 7,
  },
  actionButtons: {
    display: 'flex',
    gap: 2,
    mt: 3,
  },
  actionButtonIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 24,
    height: 24,
    borderRadius: 4,
    backgroundColor: 'custom.White',
    color: 'text.primary',
  },
  actionButtonBase: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    width: 141,
    height: 48,
    lineHeight: '48px',
    backgroundColor: 'custom.InputsBackground',
    borderRadius: 4,
  },
};
